/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Common utils for the app
 */
import _ from 'lodash';
import log from 'loglevel';
import { transliterate } from 'transliteration';

import CUATE_IMAGE from 'assets/onboarding/cuate.svg';
import VERITABLE_IMAGE from 'assets/onboarding/veritable.svg';
import DOCTOR_IMAGE from 'assets/onboarding/doctorImage.svg';
import UNLOCK_FEATURES from 'assets/onboarding/unlockFeatures.svg';
import APP_LOGO from 'assets/veritable-logo.svg';
import FALLBACK_IMAGE from 'assets/fallback-image.svg';
import { AntClass } from 'app-constants/antd';
import { PLATFORMS } from 'app-constants/index';

/**
 * getAppLogo
 */
const getAppLogo = (): string => APP_LOGO;

/**
 * Finds tenant name
 * @returns {string} tenantName
 */
const getTenantName = (): string => {
	const hostname = globalThis.location.hostname;
	const isDev = _.includes(hostname, 'localhost');
	const tenant = _.first(_.split(hostname, '.'));
	return isDev ? DEV_REALM || 'test1' : (tenant ?? '');
};

/**
 * Finds keycloak realm name as per tenant
 * @returns {string} tenantName
 */
const getRealmName = (): string => {
	const tenantName = getTenantName();
	return tenantName ? `veritable_${tenantName}` : '';
};

/**
 * get browser platform / os
 */
const getPlatform = (): UniversalSearch.Platform => {
	if (_.startsWith(_.toLower(navigator.platform), PLATFORMS.mac)) {
		return PLATFORMS.mac;
	}
	return PLATFORMS.other;
};

/**
 * translate to ascii
 */
const translateToAscii = (e: Record<string, any>): void => {
	e.target.value = transliterate(e.target.value);
};

/**
 * scroll to an element by id
 */
const scrollToElement = (elementID?: string): void => {
	setTimeout(() => {
		if (elementID) {
			const elementClass = 'focused-element';
			const element = document.getElementById(elementID);
			if (element) {
				element.style.scrollMarginTop = '200px';
				element.classList.add(elementClass);
				element.scrollIntoView({ block: 'start', behavior: 'smooth' });
				setTimeout(() => {
					element.classList.remove(elementClass);
					element.style.scrollMarginTop = '';
				}, 5000);
			}
		}
	}, 500);
};

/**
 * scroll to element by url hash
 */
const scrollToWidgetByHash = (): void => {
	const widgetID = location.hash.substring(1);
	if (widgetID) {
		scrollToElement(widgetID);
	}
};

const getValueFromStorage = (key: string, defaultValue: any): any => {
	let val = defaultValue;
	try {
		const stickyValue = globalThis.localStorage.getItem(key);
		if (_.isNull(stickyValue)) {
			throw new Error('404');
		}
		val = JSON.parse(stickyValue);
		if (_.isArray(val)) {
			val = _.compact(val);
		}
	} catch (e: any) {
		log.info(`[UseStickyState] ${e?.message === '404' ? 'sticky value is null' : 'Unable to parse sticky state'}`);
		globalThis.localStorage.removeItem(key);
	}

	return val;
};

const setValueToStorage = (key: string, value: any): void => {
	localStorage.setItem(key, JSON.stringify(value));
};

/**
 * scroll to form error field
 */
// TODO
const scrollToFormErrorField = (): void => {
	setTimeout(() => {
		const element = _.first(document.getElementsByClassName(AntClass.formilyErrorItem));
		if (element) {
			element.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, 100);
};

/**
 * createBlobFile
 */
const downloadBlobFile = (fileObject: BlobPart, name: string, contentType: string, event: string): string | void => {
	const link = document.createElement('a');
	const fileCreated = new Blob([fileObject], { type: contentType });

	link.href = URL.createObjectURL(fileCreated);
	if (event === 'download') {
		link.download = name;
		link.click();
	} else {
		return link.href;
	}
};

/**
 * getFallBackImage
 */
const getFallbackImage = (): string => FALLBACK_IMAGE;

// onboarding image
const eligibilityOnboardingImage = {
	getCuateImage: (): string => CUATE_IMAGE,
	getVeritableImage: (): string => VERITABLE_IMAGE,
	getDoctorImage: (): string => DOCTOR_IMAGE,
	getUnlockFeaturesImage: (): string => UNLOCK_FEATURES,
};

export {
	eligibilityOnboardingImage,
	getAppLogo,
	getTenantName,
	getRealmName,
	translateToAscii,
	scrollToWidgetByHash,
	setValueToStorage,
	getValueFromStorage,
	scrollToFormErrorField,
	downloadBlobFile,
	getFallbackImage,
	getPlatform,
	scrollToElement,
};
